import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";

import { faClock } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { getMinutes } from "date-fns";
import moment from "moment";

import type { MeetingEvent } from "~/common/MeetingSelect";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { inVideocallMode, isTouchScreen } from "~/lib/utils";
import { getDateString } from "~/lib/utils";
import { MeetingInfoModalWrapper } from "~/nav/panels/Meetings/MeetingInfoModal";
import MeetingPhaseContext from "~/stream/MeetingPhaseContext";
import { updateNote } from "~/stream/NoteCard";
import NoteTimeSelectorModal from "~/stream/NoteTimeSelectorModal";
import Button from "~/ui/button/Button";
import { useModals } from "~/ui/modals";
import toast from "~/ui/toast";
import Tooltip from "~/ui/Tooltip";

import type { MeetingDateOrTimer_note$key } from "~/__generated__/MeetingDateOrTimer_note.graphql";

import styles from "./MeetingDateOrTimer.module.css";

const MAX_TIMEOUT_DELAY = Math.pow(2, 31);
function timerFormatter(mins: number) {
	let str = "";

	if (Math.abs(mins) >= 60) {
		const quotient = Math.floor(Math.abs(mins) / 60);
		const remainder = Math.abs(mins) % 60;

		if (remainder > 0) {
			str = `${str}${quotient}h${remainder}m`;
		} else {
			str = `${str}${quotient}h`;
		}
	} else {
		str = `${str}${Math.abs(mins)}m`;
	}

	return str;
}

interface MeetingDateProps {
	createdAt: Date | null;
	isCancelled: boolean;
	event: {
		start: Date;
		end: Date;
		isAllDay: boolean;
	} | null;
}

export const MeetingDate = forwardRef<HTMLSpanElement, MeetingDateProps>(function MeetingDate(
	{ createdAt, event, isCancelled },
	ref,
) {
	const startTime = (event && event.start) || createdAt;

	const dateString = useMemo(() => {
		if (!startTime) return "";
		return getDateString(startTime);
	}, [startTime]);

	const timeString = useMemo(
		() =>
			startTime?.toLocaleTimeString("en", {
				hour: "numeric",
				minute: getMinutes(startTime) === 0 ? undefined : "2-digit",
			}),
		[startTime],
	);

	if (!startTime) return <span ref={ref}>Unscheduled Note</span>;

	return (
		<span
			ref={ref}
			className={cx(styles.meetingDateContent, {
				[styles.isCancelled]: isCancelled,
			})}
		>
			<span className={styles.dateString}>{dateString}</span>
			{!event?.isAllDay && (
				<>
					<span>&nbsp;·&nbsp;</span>
					<br />
					<span className={styles.timeString}>{timeString}</span>
				</>
			)}
		</span>
	);
});

interface MeetingDateOrTimerProps {
	note: MeetingDateOrTimer_note$key;
	inMeetingModal?: boolean;
}

export default function MeetingDateOrTimer({ note: noteKey, inMeetingModal }: MeetingDateOrTimerProps) {
	const note = useFragment(
		graphql`
			fragment MeetingDateOrTimer_note on Note {
				id
				createdAt
				eventStart
				eventEnd
				noteType
				viewerCanEdit
				stream {
					isExternal
				}
				event {
					status
				}
				eventGuid
				...NoteTimeSelectorModal_note
			}
		`,
		noteKey,
	);

	const { createdAt, eventStart: noteStart, eventEnd: noteEnd } = note;

	const { noMeeting, phase, getTimerValue, event: meetingEvent } = useContext(MeetingPhaseContext);

	const meetingDateEvent = useMemo<MeetingDateProps["event"]>(() => {
		if (noMeeting) {
			if (!noteStart || !noteEnd) return null;
			return {
				start: new Date(noteStart),
				end: new Date(noteEnd),
				isAllDay: false,
			};
		} else {
			return meetingEvent;
		}
	}, [noMeeting, noteStart, noteEnd, meetingEvent]);

	const [mins, setMins] = useState(() => Math.ceil(getTimerValue() / 60));

	useEffect(() => {
		let ticker: number;
		let tickerStarter: number;

		if (noMeeting) return;

		const startInterval = () => {
			if (noMeeting) return;

			const updateMins = () => {
				const seconds = getTimerValue();
				const minutes = Math.ceil(seconds / 60);

				setMins(prevMins => {
					if (minutes === prevMins) return prevMins;
					return minutes;
				});
			};

			updateMins();
			ticker = window.setInterval(updateMins, 1000);
		};

		const timeUntil15minBeforeInMilliseconds = (getTimerValue() - 15 * 60) * 1000;
		if (
			phase === "before" &&
			timeUntil15minBeforeInMilliseconds > 0 &&
			// Checking for upper bound of a 32-bit signed integer. This was ported from a previous implementation — I assume
			// 	it's either for number overflow or that `setTimeout` can only accept a delay so high. -Wal
			timeUntil15minBeforeInMilliseconds < MAX_TIMEOUT_DELAY
		) {
			tickerStarter = window.setTimeout(startInterval, timeUntil15minBeforeInMilliseconds);
		} else if (phase !== "after") {
			startInterval();
		}

		return () => {
			clearInterval(ticker);
			clearTimeout(tickerStarter);
		};
	}, [noMeeting, getTimerValue, phase]);

	const onChangeLinkedMeeting = React.useCallback(
		(event: MeetingEvent) => {
			const noteUpdates = {
				/* eslint-disable camelcase */
				event_guid: event.guid,
				event_title: event.title,
				event_start: event.start,
				event_end: event.end,
				/* eslint-enable camelcase */
			};

			// eslint-disable-next-line camelcase
			dispatchAnalyticsTrack("[meetings] Change associated meeting", { note_id: note.id });

			updateNote(note.id, noteUpdates, ({ updateNote }) => {
				if (updateNote && updateNote.ok) {
					if (inMeetingModal) {
						toast.success("Note is now linked.");
					} else {
						const oldDateTime = moment(note.eventStart || "");
						const scroll = oldDateTime.isValid() && oldDateTime.isBefore(event.start) ? "up" : "down";
						toast.success(`Note is now linked. You may need to scroll ${scroll} to find the note.`);
					}
				} else {
					toast.error("Note was not linked properly. Please sync your calendar and try again.");
				}
			});
		},
		[inMeetingModal, note.eventStart, note.id],
	);

	const modals = useModals();

	const openNoteTimeSelectorModal = useCallback(() => {
		if (!note.viewerCanEdit) return;
		return modals.open(({ close }) => (
			<NoteTimeSelectorModal closeModal={close} note={note} onChangeLinkedMeeting={onChangeLinkedMeeting} />
		));
	}, [modals, note, onChangeLinkedMeeting]);

	const openMeetingInfoModal = useCallback(() => {
		const {
			stream: { isExternal },
		} = note;

		const eventGuid = note.eventGuid; // For some reason we need to reassign this to a variable to make TS happy
		if (!eventGuid) return;
		modals.open(({ close }) => (
			<MeetingInfoModalWrapper
				eventGuid={eventGuid}
				onClose={close}
				note={note}
				noteType={note.noteType}
				isExternalUser={isExternal}
				onChangeLinkedMeeting={onChangeLinkedMeeting}
			/>
		));
	}, [modals, note, onChangeLinkedMeeting]);

	const isApproachingStart = phase === "before" && mins <= 15;
	const isApproachingEnd = phase === "started" && mins <= 5 && mins >= 0;
	const isOvertime = phase === "started" && mins < 0;

	if (!meetingEvent.isAllDay && !noMeeting && (phase === "started" || isApproachingStart)) {
		return (
			<Button
				variant="tertiary"
				size="slim"
				className={cx(styles.meetingTimer, {
					[styles.almostDone]: isApproachingEnd,
					[styles.overtime]: isOvertime,
				})}
				onClick={openMeetingInfoModal}
			>
				<FontAwesomeIcon icon={faClock} className={styles.icon} />
				&nbsp;
				{isApproachingStart && (
					<span className={cx({ [styles.hideIfNoSpace]: !inVideocallMode })}>Starts in&nbsp;</span>
				)}
				{timerFormatter(mins)}
				<span className={cx({ [styles.hideIfNoSpace]: !inVideocallMode })}>
					{phase === "started" && !isOvertime && <>&nbsp;left</>}
					{isOvertime && <>&nbsp;over</>}
				</span>
			</Button>
		);
	}

	const isCancelled = note.event?.status === "cancelled";

	const meetingDateLabel = (
		<MeetingDate
			createdAt={createdAt ? new Date(createdAt) : null}
			event={meetingDateEvent}
			isCancelled={isCancelled}
		/>
	);

	// Cases where the event details are not made accessible
	if (
		!note.viewerCanEdit ||
		note.stream.isExternal ||
		// Special case for note-only access, without event
		(note.eventGuid && !note.event)
	) {
		return <span className={styles.meetingDate}>{meetingDateLabel}</span>;
	}

	return (
		<Tooltip
			content={
				note.eventGuid && note.event
					? isCancelled
						? "Meeting cancelled"
						: "View event details"
					: "Change date and time"
			}
			placement="top"
			disabled={isTouchScreen}
			delay={[500, 0]}
		>
			<Button
				variant="tertiary"
				size="slim"
				className={styles.meetingDate}
				onClick={note.eventGuid && note.event ? openMeetingInfoModal : openNoteTimeSelectorModal}
			>
				{meetingDateLabel}
			</Button>
		</Tooltip>
	);
}
